import {
  CreateDispatchNote,
  DispatchNoteResponse
} from '../../../components/TwDispatchNotes/TwDispatchNotes';
import {
  SearchOrderFieldResponse,
  OrderDriverLinksResponse,
  SharedTokenResponse
} from './orders.model';
import {
  GetInvoiceNumberIdentifiersResponse,
  GetInvoiceNumberIdentifiersFilterParams,
  InvoiceCreateData,
  InvoiceResponseV2
} from '../../../providers/Invoices/Invoices.model';
import {
  LoadNotificationSettingsRequest,
  LoadNotificationSettingsResponse
} from '../../../providers/Settings/LoadNotificationSettings.model';
import { PaginateResponse } from '../../Global.model';
import { twApi } from '../api';
import {
  AutocompleteOrderSearchResponse,
  CreateDispatchNoteResponse,
  GetSignatureSourceByTokenParams,
  GetSignatureSourceParams,
  Order,
  ORDER_STATUS,
  OrderAttachment,
  OrderSharePreviewParams,
  OrdersTripsDocumentParams,
  PrefillOrderDataResponse,
  SaveOrderAsPDFParams,
  SearchOrderParams,
  SearchOrdersResponse,
  SendOrderInfoByEmailParams,
  UpdateManyOrderdAttachmentParams,
  UpdateOrderStopByTokenParams,
  UpsertOrder
} from './orders.model';
import StringUtils from '../../../utils/stringUtils';
import { SearchTripsParams, TRIP_STATUS, TripStop } from '../trips/trips.model';
import { persistentTripsAdapter, persistentTripsSelector, tripApiUtil } from '../trips/tripApi';
import { FilterParams } from '../savedViews/savedViews.model';
import { SearchFieldParams } from '..';

interface PaginateOrdersSearchResponse extends PaginateResponse<SearchOrdersResponse> {
  uncovered_count: number;
  partially_covered_count: number;
}

const ordersApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    createOrder: builder.mutation<Order, { data: UpsertOrder }>({
      query: ({ data }) => {
        return {
          url: `orders`,
          method: 'POST',
          body: data
        };
      },
      transformResponse: (response: { data: Order }) => response.data,
      invalidatesTags: [
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'OrdersField', id: 'ORDERS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Location' } // Invalidating locations as it might be fetched in CE load flow  and isUsedByOrders property might be stale after CE flow
      ]
    }),
    getOrderById: builder.query<Order, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `orders/${orderId}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: Order }) => {
        return response.data;
      },
      providesTags: (result, error, { orderId }) => [{ type: 'Orders' as const, orderId }]
    }),
    updateOrder: builder.mutation<
      Order,
      { id: string; data: UpsertOrder; searchTripParams?: SearchTripsParams; isLtl: boolean }
    >({
      query: ({ id, data, searchTripParams }) => ({
        url: `orders/${id}`,
        method: 'PUT',
        body: data
      }),
      onQueryStarted: async ({ id, data, searchTripParams }, { dispatch, queryFulfilled }) => {
        // update trips status column
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      transformResponse: (response: { data: Order }) => response.data,
      invalidatesTags: (result, error, { id, data }) => {
        const tripTags =
          data?.trips?.reduce((tags, trip) => {
            if (trip.id) tags.push({ type: 'Trips' as const, id: trip.id });
            return tags;
          }, [] as any) ?? [];

        return [
          { type: 'Orders' as const, id },
          { type: 'OrdersField' as const, id },
          { type: 'Orders', id: 'ORDERS_LIST' },
          { type: 'Trips', id: 'TRIPS_LIST' },
          { type: 'ActiveTrips', id: 'TRIPS_LIST' },
          { type: 'Location' },
          ...tripTags
        ];
      }
    }),
    patchOrder: builder.mutation<
      Order,
      {
        id: string;
        data: Partial<UpsertOrder>;
        searchTripParams?: SearchTripsParams;
      }
    >({
      query: ({ id, data }) => ({
        url: `orders/${id}`,
        method: 'PATCH',
        body: data
      }),
      onQueryStarted: async ({ id, data, searchTripParams }, { dispatch, queryFulfilled }) => {
        // update trips status column
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      transformResponse: (response: { data: Order }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Orders' as const, id },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Location' }
      ]
    }),
    deleteOrder: builder.mutation<
      { message: string; data: { other_expenses_updated: number } },
      { id: string; searchTripParams?: SearchTripsParams }
    >({
      query: ({ id, searchTripParams }) => ({
        url: `orders/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: async ({ id, searchTripParams }, { dispatch, queryFulfilled }) => {
        // update trips status column
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Orders' as const, id },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'OrdersField', id: 'ORDERS_LIST' },
        { type: 'OrdersField' as const, id },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    searchOrders: builder.query<PaginateOrdersSearchResponse, SearchOrderParams>({
      query: (searchLoadParams) => {
        const { filters, params } = searchLoadParams;

        return {
          url: 'orders/search',
          method: 'POST',
          body: filters,
          params: params
        };
      },
      transformResponse: (response: {
        data: SearchOrdersResponse[];
        total: number;
        more_records: boolean;
        uncovered_count: number;
        partially_covered_count: number;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records,
          uncovered_count: response.uncovered_count,
          partially_covered_count: response.partially_covered_count
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Orders' as const, id })),
              { type: 'Orders', id: 'ORDERS_LIST' }
            ]
          : [{ type: 'Orders', id: 'ORDERS_LIST' }]
    }),
    updateOrderStatus: builder.mutation<
      Order,
      {
        id: string;
        status: ORDER_STATUS;
        searchTripParams?: SearchTripsParams;
      }
    >({
      query: ({ id, status }) => ({
        url: `orders/${id}/status`,
        method: 'POST',
        body: { status }
      }),
      onQueryStarted: async ({ searchTripParams }, { dispatch, queryFulfilled }) => {
        // update trips status column
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      transformResponse: (response: { data: Order }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Orders' as const, id },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Location' }
      ]
    }),
    uploadOrderTripDocument: builder.mutation<
      number,
      {
        document: OrdersTripsDocumentParams;
        dynamsoft?: boolean;
        searchTripParams?: SearchTripsParams;
        status?: string;
        reviewed?: boolean;
      }
    >({
      query: (data) => {
        const formData = new FormData();
        let url = '/trip-order-attachments/upload';

        formData.append('attachment', data.document.document);
        formData.append('type', data.document.type);
        formData.append('visibility', data.document.visibility ? 'true' : 'false');
        formData.append('entity', data.document.entity);
        if (data.document.orderId) {
          formData.append('orderId', data.document.orderId);
        }

        if (data.document.tripId) {
          formData.append('tripId', data.document.tripId);
        }

        if (data.document.orderRequestId) {
          formData.append('orderRequestId', data.document.orderRequestId);
        }

        if (data.reviewed) {
          formData.append('reviewed', data.reviewed ? 'true' : 'false');
        }

        if (data.dynamsoft) {
          url = '/trip-order-attachments/upload/?source=dynamsoft';
        }

        return {
          url: url,
          formData: true,
          method: 'POST',
          body: formData
        };
      },
      onQueryStarted: async ({ searchTripParams, status }, { dispatch, queryFulfilled }) => {
        if (searchTripParams && status) {
          // update trips status column
          const updates = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: {
                  ...searchTripParams?.filters,
                  status: [status as TRIP_STATUS]
                }
              },
              (tripsData) => {
                persistentTripsAdapter.removeAll(tripsData.content);
              }
            )
          );
          queryFulfilled.catch(() => {
            updates.undo();
          });
        }
      },
      transformResponse: (response: number) => {
        return response;
      },
      invalidatesTags: (result, error, { document }) => [
        { type: 'Orders' as const, id: document.orderId || undefined },
        { type: 'Trips' as const, id: document.tripId || undefined },
        { type: 'ActiveTrips' as const, id: document.tripId || undefined },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    uploadOrderTripDocumentByToken: builder.mutation<
      number,
      {
        document: OrdersTripsDocumentParams;
        token: string;
        dynamsoft?: boolean;
      }
    >({
      query: (data) => {
        const formData = new FormData();
        let url = `orders/t/${data.token}/attachments`;

        formData.append('attachment', data.document.document);
        formData.append('type', data.document.type);
        formData.append('visibility', data.document.visibility ? 'true' : 'false');
        formData.append('entity', data.document.entity);
        if (data.document.orderId) {
          formData.append('orderId', data.document.orderId);
        }

        if (data.document.tripId) {
          formData.append('tripId', data.document.tripId);
        }

        if (data.dynamsoft) {
          url = `orders/t/${data.token}/attachments/?source=dynamsoft`;
        }

        return {
          url: url,
          formData: true,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: number) => {
        return response;
      }
    }),
    updateOrderTripDocumentById: builder.mutation<
      OrderAttachment,
      {
        data: Partial<OrdersTripsDocumentParams>;
        id: string;
        searchTripParams?: SearchTripsParams;
        status?: string;
      }
    >({
      query: ({ data, id }) => {
        return {
          url: `/trip-order-attachments/${id}`,
          method: 'PUT',
          body: data
        };
      },
      onQueryStarted: async ({ searchTripParams, status }, { dispatch, queryFulfilled }) => {
        if (searchTripParams && status) {
          // update trips status column
          const updates = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: {
                  ...searchTripParams?.filters,
                  status: [status as TRIP_STATUS]
                }
              },
              (tripsData) => {
                persistentTripsAdapter.removeAll(tripsData.content);
              }
            )
          );
          queryFulfilled.catch(() => {
            updates.undo();
          });
        }
      },
      invalidatesTags: (result, error, { data }) => [
        { type: 'Orders' as const, id: data.orderId || undefined },
        { type: 'Trips' as const, id: data.tripId || undefined },
        { type: 'ActiveTrips' as const, id: data.tripId || undefined },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    deleteOrderTripDocument: builder.mutation<
      any,
      {
        id: string;
        orderId?: string;
        tripId?: string;
        searchTripParams?: SearchTripsParams;
        status?: string;
      }
    >({
      query: ({ id, orderId, tripId }) => ({
        url: `/trip-order-attachments/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: async ({ searchTripParams, status }, { dispatch, queryFulfilled }) => {
        if (searchTripParams && status) {
          // update trips status column
          const updates = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: {
                  ...searchTripParams?.filters,
                  status: [status as TRIP_STATUS]
                }
              },
              (tripsData) => {
                persistentTripsAdapter.removeAll(tripsData.content);
              }
            )
          );
          queryFulfilled.catch(() => {
            updates.undo();
          });
        }
      },
      invalidatesTags: (result, error, { id, orderId, tripId }) => [
        { type: 'Orders' as const, orderId },
        { type: 'Trips' as const, id: tripId || 'TRIPS_LIST' },
        { type: 'ActiveTrips' as const, id: tripId || 'TRIPS_LIST' },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    deleteOrderTripDocumentByToken: builder.mutation<any, { id: string; token: string }>({
      query: ({ id, token }) => ({
        url: `/t/${token}/attachments/${id}`,
        method: 'DELETE'
      })
    }),
    updateManyDocuments: builder.mutation<any, UpdateManyOrderdAttachmentParams[]>({
      query: (data) => ({
        url: `/trip-order-attachments`,
        method: 'PUT',
        body: {
          tripOrderAttachments: data
        }
      }),
      invalidatesTags: () => [{ type: 'Attachments', id: 'ATTACHMENTS_LIST' }]
    }),
    postDispatchNote: builder.mutation<CreateDispatchNoteResponse, CreateDispatchNote>({
      query: (data) => {
        const { orderId, tripId, token, note, note_type } = data;
        let url = '';
        const body: any = {
          note: note,
          note_type: note_type,
          orderId: orderId,
          tripId: tripId
        };

        if (orderId && !token) {
          url = `/dispatch-notes/orders/${orderId}`;
        }

        if (tripId && !token) {
          url = `/dispatch-notes/trips/${tripId}`;
        }

        if (token) {
          url = `/dispatch-notes/p/${token}`;
          body.trip_id = tripId;
        }

        return {
          url: url,
          method: 'POST',
          body: body
        };
      },
      invalidatesTags: [{ type: 'DispatchNotes', id: 'NOTES_LIST' }]
    }),
    getDispatchNotes: builder.query<
      DispatchNoteResponse[],
      { orderId?: string; tripId?: string; token?: string; order: 'asc' | 'desc' }
    >({
      query: (data) => {
        const { orderId, tripId, token, order } = data;

        let url = '';

        if (orderId && !token) {
          url = `/dispatch-notes/orders/${orderId}/?order=${order}`;
        }
        if (tripId && !token) {
          url = `/dispatch-notes/trips/${tripId}/?order=${order}`;
        }

        if (token) {
          url = `/dispatch-notes/p/${token}/?order=${order}`;
        }

        return {
          url: url,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: DispatchNoteResponse[] }) => {
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ dispatch_note: { id } }) => ({
                type: 'DispatchNotes' as const,
                id
              })),
              { type: 'DispatchNotes', id: 'NOTES_LIST' }
            ]
          : [{ type: 'DispatchNotes', id: 'NOTES_LIST' }]
    }),
    deleteDispatchNote: builder.mutation<any, { noteId: string; token?: string }>({
      query: (data) => {
        const { noteId, token } = data;
        let url = '';
        if (noteId && !token) {
          url = `/dispatch-notes/${noteId}`;
        }

        return {
          url: url,
          method: 'DELETE'
        };
      },
      invalidatesTags: [{ type: 'DispatchNotes', id: 'NOTES_LIST' }]
    }),
    createInvoice: builder.mutation<
      { status: number; message: string; data: InvoiceResponseV2 },
      {
        data: { invoiceData: InvoiceCreateData };
        searchTripParams?: SearchTripsParams;
      }
    >({
      query: (data) => {
        return {
          url: 'v3/invoices',
          method: 'POST',
          body: data.data.invoiceData
        };
      },
      onQueryStarted: async ({ searchTripParams }, { dispatch, queryFulfilled }) => {
        // update trips status column
        const update1 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DRAFT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update2 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DISPATCHED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update3 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.IN_TRANSIT] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );
        const update4 = dispatch(
          tripApiUtil.updateQueryData(
            'getActivePersistentTrips',
            {
              ...searchTripParams,
              filters: { ...searchTripParams?.filters, status: [TRIP_STATUS.DELIVERED] }
            },
            (tripsData) => {
              persistentTripsAdapter.removeAll(tripsData.content);
            }
          )
        );

        queryFulfilled.catch(() => {
          update1.undo();
          update2.undo();
          update3.undo();
          update4.undo();
        });
      },
      transformResponse: (response: {
        status: number;
        message: string;
        data: InvoiceResponseV2;
      }) => {
        return response;
      },
      invalidatesTags: (result, error) => [
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    deleteInvoice: builder.mutation<number, string>({
      query: (id) => ({
        url: `v3/invoices/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (_, meta: { response: { status: number } }) => {
        return meta.response.status;
      },
      invalidatesTags: (result, error) => [
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    getOrderNotificationSettings: builder.query<LoadNotificationSettingsResponse, { id?: string }>({
      query: (data) => {
        if (data.id) {
          return {
            url: `v3/order-status-notification-configurations/?id=${data.id}`,
            method: 'GET'
          };
        } else {
          return {
            url: `v3/order-status-notification-configurations`,
            method: 'GET'
          };
        }
      },
      transformResponse: (response: { data: LoadNotificationSettingsResponse }) => {
        return response.data;
      }
    }),
    updateOrdersNotificationSettings: builder.mutation<any, LoadNotificationSettingsRequest>({
      query: (data) => {
        return {
          url: `v3/order-status-notification-configurations`,
          method: 'PUT',
          body: data
        };
      }
    }),
    shareOrderPreview: builder.mutation<
      number,
      { id: string; instructions: OrderSharePreviewParams; searchParams?: any }
    >({
      query: ({ id, instructions, searchParams }) => ({
        url: `orders/${id}/share-preview`,
        method: 'POST',
        body: instructions
      }),
      /*  onQueryStarted: async ({ id, instructions, searchParams }, { dispatch, queryFulfilled }) => {
      const filters = searchParams?.filters;

      let entity: any = null;

      // update previous status column (Draft)
      const update1 = dispatch(
        loadApi.util.updateQueryData(
          'getPersistentCachedLoads',
          { filters: { ...filters, status: [LoadStatus.draft] } },
          (loadsData) => {
            const load = persistentLoadSelector.selectById(loadsData.content, id);
            entity = load;
            persistentLoadsAdapter.removeOne(loadsData.content, id);
          }
        )
      );

      // update current status column
      const update2 = dispatch(
        loadApi.util.updateQueryData(
          'getPersistentCachedLoads',
          { filters: { ...filters, status: [LoadStatus.dispatched] } },
          (loadsData) => {
            persistentLoadsAdapter.addOne(loadsData.content, {
              ...entity,
              status: LoadStatus.dispatched
            });
          }
        )
      );

      queryFulfilled.catch(() => {
        update1.undo();
        update2.undo();
      });
    }, */
      transformResponse: (_, meta: { response: { status: number } }) => {
        return meta.response.status;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' }
      ]
    }),
    getPublicOrder: builder.query<SharedTokenResponse, string>({
      query: (token) => ({
        url: `orders/t/${token}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: SharedTokenResponse }) => {
        return response.data;
      }
    }),
    updateOrderStopByToken: builder.mutation<
      any,
      { token: string; data: UpdateOrderStopByTokenParams }
    >({
      query: ({ token, data }) => {
        return {
          url: `orders/t/${token}`,
          method: 'PUT',
          body: data
        };
      }
    }),
    getPublicSignature: builder.query<any, { token: string; stopId: string }>({
      query: ({ token, stopId }) => ({
        url: `/orders/t/${token}/stops/${stopId}/signature`,
        method: 'GET'
      })
    }),
    savePublicSignature: builder.mutation<
      any,
      { token: string; stopId: string; signatureFile: Blob; fullname: string }
    >({
      query: ({ token, stopId, signatureFile, fullname }) => {
        const formData = new FormData();

        formData.append('signature', signatureFile);
        formData.append('full_name', fullname);

        return {
          url: `/orders/t/${token}/stops/${stopId}/signature`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    saveSignature: builder.mutation<
      any,
      { orderId: string; stopId: string; signatureFile: Blob; fullname: string }
    >({
      query: ({ orderId, stopId, signatureFile, fullname }) => {
        const formData = new FormData();

        formData.append('signature', signatureFile);
        formData.append('full_name', fullname);

        return {
          url: `/orders/${orderId}/stops/${stopId}/signature`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    getSignature: builder.query<any, { orderId: string; stopId: string }>({
      query: ({ orderId, stopId }) => ({
        url: `/orders/${orderId}/stops/${stopId}/signature`,
        method: 'GET'
      })
    }),
    saveOrderAsPDF: builder.mutation<Blob, SaveOrderAsPDFParams>({
      query: ({ orderId, attachments, include_invoice }) => ({
        url: `/orders/${orderId}/pdf`,
        method: 'POST',
        body: { attachments, include_invoice },
        responseHandler: (response): Promise<Blob> => response.blob()
      })
    }),
    sendOrderInfoByEmail: builder.mutation<
      Blob,
      { orderId: string; params: SendOrderInfoByEmailParams }
    >({
      query: ({ orderId, params }) => ({
        url: `/orders/${orderId}/send-email`,
        method: 'POST',
        body: { ...params },
        responseHandler: (response): Promise<Blob> => response.blob()
      })
    }),
    getBulkOrderImportTemplate: builder.mutation({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/orders/import/template`,
          method: 'GET',
          responseHandler: (response) => response.blob()
        });
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;
        const blobCSV = url.createObjectURL(result.data as Blob);
        hiddenElement.href = blobCSV;
        hiddenElement.target = '_blank';
        hiddenElement.download = `order-import-template.csv`;
        hiddenElement.click();

        setTimeout(() => {
          if (document.body.contains(hiddenElement)) {
            document.body.removeChild(hiddenElement);
          }
          url.revokeObjectURL(blobCSV);
        }, 100);
        return { data: null };
      }
    }),
    importBulkOrderCSV: builder.mutation<any, { file: File; divisionId: string | undefined }>({
      query: ({ file, divisionId }) => {
        const formData = new FormData();
        let url = 'orders/import';

        if (divisionId) {
          url = `${url}?divisionId=${divisionId}`;
        }

        formData.append('file', file);

        return {
          url: url,
          formData: true,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: number) => {
        return response;
      }
    }),
    exportBulkOrderCSV: builder.mutation<Blob, FilterParams>({
      query: (filters) => {
        return {
          url: 'orders/csv',
          method: 'POST',
          body: filters,
          responseHandler: (response): any => response.text()
        };
      },
      transformResponse: (response: any) => {
        return new Blob([response]);
      }
    }),
    getStopSignatureSource: builder.mutation<Blob, GetSignatureSourceParams>({
      query: ({ orderId, stopId, signatureId }) => ({
        url: `/orders/${orderId}/stops/${stopId}/signature/${signatureId}/files-vysor`,
        method: 'GET',
        responseHandler: (response): Promise<Blob> => response.blob()
      })
    }),
    getStopSignatureSourceByToken: builder.mutation<Blob, GetSignatureSourceByTokenParams>({
      query: ({ token, stopId, signatureId }) => ({
        url: `/orders/t/${token}/stops/${stopId}/signature/${signatureId}/files-vysor`,
        method: 'GET',
        responseHandler: (response): Promise<Blob> => response.blob()
      })
    }),
    canOrdersBeDeleted: builder.query<
      {
        status: boolean;
        message: string;
      },
      FilterParams
    >({
      query: (filterParams) => ({
        url: 'orders/can-orders-be-deleted',
        method: 'POST',
        body: filterParams
      }),
      transformResponse: (response: { status: boolean; message: string }) => response
    }),
    bulkDeleteOrders: builder.mutation<
      {
        success: boolean;
        data: {
          ordersDeleted: number;
          other_expenses_updated: number;
        };
      },
      FilterParams
    >({
      query: (filters) => ({
        url: 'orders/bulk-delete',
        method: 'DELETE',
        body: filters
      }),
      transformResponse: (response: {
        success: boolean;
        data: {
          ordersDeleted: number;
          other_expenses_updated: number;
        };
      }) => response,
      invalidatesTags: (result, error) => [
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    searchOrderInTrip: builder.mutation<
      AutocompleteOrderSearchResponse[],
      {
        searchText?: string;
        readable_id?: string;
        customer_order_number?: string;
      }
    >({
      query: (data) => ({
        url: 'orders/autocomplete-order',
        method: 'POST',
        body: data
      }),
      transformResponse: (response: { data: AutocompleteOrderSearchResponse[] }) => {
        return response.data;
      }
    }),
    prefillOrderLTLStopsById: builder.query<PrefillOrderDataResponse, string>({
      query: (orderId) => ({
        url: `orders/ltl-prefill/${orderId}`,
        method: 'GET'
      }),
      transformResponse: (response: { data: PrefillOrderDataResponse }) => response.data
    }),
    getOrdersForInvoice: builder.mutation<Order[], string[]>({
      query: (orderIds) => ({
        url: 'orders/get-orders-for-invoice',
        method: 'POST',
        body: {
          orderIds: orderIds
        }
      }),
      transformResponse: (response: { data: Order[] }) => response.data
    }),
    uncancelOrder: builder.mutation<Order, string>({
      query: (orderId) => ({
        url: `orders/${orderId}/uncancel-order`,
        method: 'PATCH'
      }),
      invalidatesTags: (result, error, orderId) => [
        { type: 'Orders' as const, orderId },
        { type: 'Orders', id: 'ORDERS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' },
        { type: 'Location' }
      ]
    }),
    searchOrderField: builder.query<PaginateResponse<SearchOrderFieldResponse>, SearchFieldParams>({
      query: (searchLoadParams) => {
        const { search, field, params } = searchLoadParams;

        return {
          url: 'orders/fields/search',
          method: 'POST',
          body: { search, field },
          params: params
        };
      },
      transformResponse: (response: {
        data: SearchOrderFieldResponse[];
        total: number;
        more_records: boolean;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'OrdersField' as const, id })),
              { type: 'OrdersField', id: 'ORDERS_LIST' }
            ]
          : [{ type: 'OrdersField', id: 'ORDERS_LIST' }]
    }),
    bulkCreateOrder: builder.mutation<any, { data: { source: string; ids: string[] } }>({
      query: ({ data }) => {
        return {
          url: `orders/bulk-create`,
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: [{ type: 'EDI', id: 'EDI_LIST' }]
    }),
    getDriverLinksForOrder: builder.query<OrderDriverLinksResponse, string>({
      query: (orderId) => ({
        url: `orders/${orderId}/driver-links`,
        method: 'GET'
      }),
      transformResponse: (response: { data: OrderDriverLinksResponse }) => {
        return response.data;
      }
    }),
    // TODO: move this api to invoicesApi when it's available
    getInvoiceNumberIdentifiers: builder.query<
      GetInvoiceNumberIdentifiersResponse[],
      {
        filters?: GetInvoiceNumberIdentifiersFilterParams;
      }
    >({
      query: ({ filters }) => {
        return {
          url: '/v3/invoices/invoice-number-identifiers',
          method: 'POST',
          body: {
            filters
          }
        };
      },
      providesTags: () => [
        { type: 'InvoiceNumberIdentifiers' as const, id: 'INVOICE_NUMBER_IDENTIFIERS' }
      ],
      transformResponse: (response: {
        data: GetInvoiceNumberIdentifiersResponse[];
      }): GetInvoiceNumberIdentifiersResponse[] => {
        return response.data;
      }
    })
  }),
  overrideExisting: false
});

export const {
  useCreateOrderMutation,
  useGetOrderByIdQuery,
  useLazyGetOrderByIdQuery,
  useUpdateOrderMutation,
  usePatchOrderMutation,
  useDeleteOrderMutation,
  useUpdateOrderStatusMutation,
  useUploadOrderTripDocumentMutation,
  useUpdateOrderTripDocumentByIdMutation,
  useDeleteOrderTripDocumentMutation,
  useUpdateManyDocumentsMutation,
  useSearchOrdersQuery,
  usePostDispatchNoteMutation,
  useGetDispatchNotesQuery,
  useDeleteDispatchNoteMutation,
  useCreateInvoiceMutation,
  useDeleteInvoiceMutation,
  useUploadOrderTripDocumentByTokenMutation,
  useDeleteOrderTripDocumentByTokenMutation,
  useGetOrderNotificationSettingsQuery,
  useLazyGetOrderNotificationSettingsQuery,
  useUpdateOrdersNotificationSettingsMutation,
  useShareOrderPreviewMutation,
  useLazyGetPublicOrderQuery,
  useLazyGetPublicSignatureQuery,
  useSavePublicSignatureMutation,
  useLazyGetSignatureQuery,
  useSaveSignatureMutation,
  useSaveOrderAsPDFMutation,
  useSendOrderInfoByEmailMutation,
  useGetBulkOrderImportTemplateMutation,
  useImportBulkOrderCSVMutation,
  useExportBulkOrderCSVMutation,
  useGetStopSignatureSourceMutation,
  useGetStopSignatureSourceByTokenMutation,
  useCanOrdersBeDeletedQuery,
  useBulkDeleteOrdersMutation,
  useSearchOrderInTripMutation,
  useLazyPrefillOrderLTLStopsByIdQuery,
  useGetOrdersForInvoiceMutation,
  useUpdateOrderStopByTokenMutation,
  useUncancelOrderMutation,
  useSearchOrderFieldQuery,
  useLazySearchOrderFieldQuery,
  useBulkCreateOrderMutation,
  useGetDriverLinksForOrderQuery,
  useGetInvoiceNumberIdentifiersQuery
} = ordersApi;
